import { ReactElement, useCallback } from 'react';

import {
   Status,
   WaitlistEntryCancelAppointmentCommand,
   WaitlistEntryQuery,
   WaitlistEntrySuspendCommand,
} from '@dreryk/egabinet-waitlist-frontend-api';
import { AppointmentId } from '@dreryk/egabinet-appointment-published-language-api';

import { dispatchSnackbarPositiveEvent, Dropdown } from '@dreryk/react-components';
import { Translate } from '@dreryk/react-common';
import { refetchCqrsQuery, useCqrsCommand } from '@dreryk/command-query-react-common';

import { translationKeys } from '../../../translations';

type SuspendEntryDropdownItemProps = {
   readonly entryId: number;
   readonly appointmentId: AppointmentId | undefined;
   readonly disabled: boolean;
   readonly entryStatus: Status;
};

export const SuspendEntryDropdownItem = (props: SuspendEntryDropdownItemProps): ReactElement => {
   const { executeAsync: suspendEntry } = useCqrsCommand<void, void>(
      () => {
         const command = new WaitlistEntrySuspendCommand();
         command.entryId = props.entryId;
         return command;
      },
      {
         onSuccess: () => {
            dispatchSnackbarPositiveEvent(translationKeys.successfullySuspendedFromWaitlist);
            refetchCqrsQuery(WaitlistEntryQuery);
         },
      }
   );

   const { executeAsync: cancelAppointment } = useCqrsCommand<void, void>(
      () => {
         const command = new WaitlistEntryCancelAppointmentCommand();
         command.appointmentId = props.appointmentId!;
         command.targetStatus = 'SUSPENDED';
         return command;
      },
      {
         onSuccess: () => {
            dispatchSnackbarPositiveEvent(translationKeys.successfullyCancelledAppointmentAndSuspendedFromWaitlist);
            refetchCqrsQuery(WaitlistEntryQuery);
         },
      }
   );

   const handleClick = useCallback(
      async (hide) => {
         if (props.entryStatus === 'REGISTERED') {
            await cancelAppointment();
            hide();
            return;
         }
         await suspendEntry();
         hide();
      },
      [props.entryStatus, suspendEntry, cancelAppointment]
   );

   return (
      <Dropdown.Item testId={'suspendEntry'} disabled={props.disabled} onClick={handleClick}>
         <Translate>{translationKeys.suspendEntry}</Translate>
      </Dropdown.Item>
   );
};
